import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  CloudDownload,
  Email,
  Settings,
  Refresh,
  GetApp,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useGetTwilioConfigurationsQuery,
  useCreateTwilioConfigurationMutation,
  useUpdateTwilioConfigurationMutation,
  useSyncTwilioDataMutation,
  useGetTwilioStatisticsQuery,
  useExportTwilioDataMutation,
} from '../store/api/twilioApi';
import { useSendEmailReportMutation } from '../store/api/emailApi';
import { 
  useGetAutoSyncStatusQuery,
  useUpdateAutoSyncSettingsMutation 
} from '../store/api/autoSyncApi';
import { 
  useGetJobRunsQuery,
  useGetJobRunStatsQuery 
} from '../store/api/jobRunsApi';
import { useGetEmailAddressesQuery } from '../store/api/emailAddressesApi';
import { useSelector } from 'react-redux';

// Timezone formatting function
const formatDateTime = (dateString: string, userTimezone: string = 'US/Eastern') => {
  if (!dateString) return 'N/A';
  const utcDate = new Date(dateString);
  
  // Use Intl.DateTimeFormat for proper timezone conversion
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const formattedDate = parts.map(part => part.value).join('');
  
  // Get timezone abbreviation
  const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    timeZoneName: 'short'
  });
  
  const timeZoneName = timeZoneFormatter.formatToParts(utcDate)
    .find(part => part.type === 'timeZoneName')?.value || '';
  
  return `${formattedDate} ${timeZoneName}`;
};

// Form validation schema
const twilioConfigSchema = yup.object({
  name: yup.string().required('Configuration name is required'),
  account_sid: yup.string().required('Account SID is required'),
  auth_token: yup.string().required('Auth token is required'),
  sync_interval_hours: yup.number().min(0.01, 'Sync interval must be at least 0.01 hours').required(),
  max_pages_per_run: yup.number().min(1, 'Max pages must be at least 1').required(),
  page_size: yup.number().min(1, 'Page size must be at least 1').required(),
  email_to: yup.string().nullable(),
  email_cc: yup.string().nullable(),
  email_bcc: yup.string().nullable(),
  is_active: yup.boolean(),
  auto_sync_enabled: yup.boolean(),
});

type TwilioConfigFormData = yup.InferType<typeof twilioConfigSchema>;

export const TwilioExporterPage: React.FC = () => {
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);

  // Check authentication
  const { token, isAuthenticated, user } = useSelector((state: any) => state.auth);
  const userTimezone = user?.timezone || 'US/Eastern';

  // API hooks
  const { data: configurations, isLoading: configsLoading } = useGetTwilioConfigurationsQuery();
  const [createConfig, { isLoading: createLoading }] = useCreateTwilioConfigurationMutation();
  const [updateConfig, { isLoading: updateLoading }] = useUpdateTwilioConfigurationMutation();
  const [syncData, { isLoading: syncLoading }] = useSyncTwilioDataMutation();
  const [exportData, { isLoading: exportLoading }] = useExportTwilioDataMutation();
  const [sendEmailReport, { isLoading: emailLoading }] = useSendEmailReportMutation();
  const [updateAutoSyncSettings] = useUpdateAutoSyncSettingsMutation();
  
  const { data: statistics } = useGetTwilioStatisticsQuery(selectedConfigId!, {
    skip: !selectedConfigId,
  });

  const { data: autoSyncStatus } = useGetAutoSyncStatusQuery(selectedConfigId!, {
    skip: !selectedConfigId,
  });

  // Get job runs for the selected configuration
  const { data: jobRuns, isLoading: jobRunsLoading } = useGetJobRunsQuery(
    { 
      configuration_id: selectedConfigId!,
      page: 1,
      page_size: 5
    },
    { skip: !selectedConfigId }
  );

  // Get job run statistics
  const { data: jobRunStats } = useGetJobRunStatsQuery(
    { configuration_id: selectedConfigId! },
    { skip: !selectedConfigId }
  );

  // Get email addresses for dropdown selection
  const { data: emailAddresses = [] } = useGetEmailAddressesQuery();

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<TwilioConfigFormData>({
    resolver: yupResolver(twilioConfigSchema),
    defaultValues: {
      name: '',
      account_sid: '',
      auth_token: '',
      sync_interval_hours: 1,
      max_pages_per_run: 50,
      page_size: 500,
      email_to: '',
      email_cc: '',
      email_bcc: '',
      is_active: false,
      auto_sync_enabled: false,
    }
  });

  // Load configuration data when available - ONLY on initial load
  useEffect(() => {
    console.log('Configurations loaded:', configurations);
    if (configurations && configurations.length > 0 && !selectedConfigId) {
      const config = configurations[0]; // Use first configuration for now
      console.log('Initial load - setting form with configuration data:', config);
      setSelectedConfigId(config.id);
      reset({
        name: config.name,
        account_sid: config.account_sid,
        auth_token: config.auth_token,
        sync_interval_hours: config.sync_interval_hours,
        max_pages_per_run: config.max_pages_per_run,
        page_size: config.page_size,
        email_to: config.email_to || '',
        email_cc: config.email_cc || '',
        email_bcc: config.email_bcc || '',
        is_active: config.is_active,
        auto_sync_enabled: config.auto_sync_enabled || false,
      });
    } else if (configurations && configurations.length === 0) {
      console.log('No configurations found');
    }
  }, [configurations, reset, selectedConfigId]);


  const onSubmit = async (data: TwilioConfigFormData) => {
    try {
      console.log('Submitting form data:', data);
      console.log('Selected config ID:', selectedConfigId);
      console.log('Authentication status:', { token: !!token, isAuthenticated });
      
      if (!token || !isAuthenticated) {
        setErrorMessage('Please log in to save configuration');
        setShowError(true);
        return;
      }
      
      // Convert null values to undefined for API compatibility
      const apiData = {
        ...data,
        email_to: data.email_to || undefined,
        email_cc: data.email_cc || undefined,
        email_bcc: data.email_bcc || undefined,
      };
      
      if (selectedConfigId) {
        console.log('Updating existing configuration...');
        await updateConfig({ id: selectedConfigId, data: apiData }).unwrap();
      } else {
        console.log('Creating new configuration...');
        await createConfig(apiData).unwrap();
      }
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      let errorMsg = 'Failed to save configuration';
      if (error?.data?.detail) {
        if (typeof error.data.detail === 'string') {
          errorMsg = error.data.detail;
        } else if (Array.isArray(error.data.detail)) {
          errorMsg = error.data.detail.map((err: any) => err.msg || err).join(', ');
        } else {
          errorMsg = JSON.stringify(error.data.detail);
        }
      }
      setErrorMessage(errorMsg);
      setShowError(true);
    }
  };

  const handleSync = async () => {
    if (!selectedConfigId) {
      setErrorMessage('Please select a configuration first');
      setShowError(true);
      return;
    }
    
    // Check if configuration has required fields
    const currentConfig = configurations?.find(c => c.id === selectedConfigId);
    if (!currentConfig) {
      setErrorMessage('Configuration not found');
      setShowError(true);
      return;
    }
    
    if (!currentConfig.account_sid || !currentConfig.auth_token) {
      setErrorMessage('Please save the configuration with Account SID and Auth Token before syncing');
      setShowError(true);
      return;
    }
    
    try {
      await syncData({ configId: selectedConfigId, forceFullSync: false }).unwrap();
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Error syncing data:', error);
      console.error('Error data:', error?.data);
      console.error('Error detail:', error?.data?.detail);
      let errorMsg = 'Failed to sync data';
      if (error?.data?.detail) {
        if (typeof error.data.detail === 'string') {
          errorMsg = error.data.detail;
        } else if (Array.isArray(error.data.detail)) {
          errorMsg = error.data.detail.map((err: any) => err.msg || err).join(', ');
        } else {
          errorMsg = JSON.stringify(error.data.detail);
        }
      }
      setErrorMessage(errorMsg);
      setShowError(true);
    }
  };

  const handleExport = async () => {
    if (!selectedConfigId) {
      setErrorMessage('Please select a configuration first');
      setShowError(true);
      return;
    }
    
    try {
      const exportRequest = {
        format: 'csv' as const,
        include_messages: true,
        include_calls: true,
        date_from: undefined,
        date_to: undefined,
      };
      
      const result = await exportData({ 
        configId: selectedConfigId, 
        data: exportRequest 
      }).unwrap();
      
      if (result.success) {
        setErrorMessage(`Export successful! ${result.message}`);
        setShowSuccess(true);
        
        // If there's a download URL, open it
        if (result.download_url) {
          window.open(result.download_url, '_blank');
        }
      } else {
        setErrorMessage(result.message || 'Export failed');
        setShowError(true);
      }
    } catch (error: any) {
      console.error('Error exporting data:', error);
      let errorMsg = 'Failed to export data';
      if (error?.data?.detail) {
        if (typeof error.data.detail === 'string') {
          errorMsg = error.data.detail;
        } else if (Array.isArray(error.data.detail)) {
          errorMsg = error.data.detail.map((err: any) => err.msg || err).join(', ');
        } else {
          errorMsg = JSON.stringify(error.data.detail);
        }
      }
      setErrorMessage(errorMsg);
      setShowError(true);
    }
  };

  const handleEmailReport = () => {
    if (!selectedConfigId) {
      setErrorMessage('Please select a configuration first');
      setShowError(true);
      return;
    }
    
    // Check if the selected configuration is active
    const selectedConfig = configurations?.find(config => config.id === selectedConfigId);
    if (!selectedConfig?.is_active) {
      setErrorMessage('Please activate the selected configuration first. Click the "Active" toggle to enable it.');
      setShowError(true);
      return;
    }
    
    // Check if email recipients are configured
    if (!selectedConfig.email_to && !selectedConfig.email_cc && !selectedConfig.email_bcc) {
      setErrorMessage('Please configure email recipients (TO, CC, or BCC) in the configuration settings first.');
      setShowError(true);
      return;
    }
    
    // Show confirmation dialog
    setShowEmailConfirmation(true);
  };

  const confirmEmailReport = async () => {
    setIsEmailSending(true);
    setShowEmailConfirmation(false);
    
    try {
      const emailRequest = {
        config_id: selectedConfigId!,
        report_type: 'daily' as const,
      };
      
      // Add a small delay to show the spinning button
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      const result = await sendEmailReport(emailRequest).unwrap();
      
      if (result.success) {
        setErrorMessage(`Email report sent successfully! ${result.message}`);
        setShowSuccess(true);
      } else {
        setErrorMessage(result.message || 'Failed to send email report');
        setShowError(true);
      }
    } catch (error: any) {
      console.error('Error sending email report:', error);
      let errorMsg = 'Failed to send email report';
      if (error?.data?.detail) {
        if (typeof error.data.detail === 'string') {
          errorMsg = error.data.detail;
        } else if (Array.isArray(error.data.detail)) {
          errorMsg = error.data.detail.map((err: any) => err.msg || err).join(', ');
        } else {
          errorMsg = JSON.stringify(error.data.detail);
        }
      }
      setErrorMessage(errorMsg);
      setShowError(true);
    } finally {
      setIsEmailSending(false);
    }
  };

  const cancelEmailReport = () => {
    setShowEmailConfirmation(false);
  };

  const handleAutoSyncToggle = async (enabled: boolean) => {
    if (!selectedConfigId) {
      setErrorMessage('Please select a configuration first');
      setShowError(true);
      return;
    }

    try {
      await updateAutoSyncSettings({
        configId: selectedConfigId,
        settings: {
          auto_sync_enabled: enabled,
          sync_interval_hours: configurations?.find(c => c.id === selectedConfigId)?.sync_interval_hours || 1
        }
      }).unwrap();
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Error updating auto-sync settings:', error);
      let errorMsg = 'Failed to update auto-sync settings';
      if (error?.data?.detail) {
        if (typeof error.data.detail === 'string') {
          errorMsg = error.data.detail;
        } else if (Array.isArray(error.data.detail)) {
          errorMsg = error.data.detail.map((err: any) => err.msg || err).join(', ');
        } else {
          errorMsg = JSON.stringify(error.data.detail);
        }
      }
      setErrorMessage(errorMsg);
      setShowError(true);
    }
  };

  const formatEasternTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }) + ' EST';
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (configsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Twilio Data Exporter
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Export SMS and call data from Twilio
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
        {/* Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Twilio Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Configuration Name"
                        placeholder="Enter configuration name"
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="account_sid"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Account SID"
                        placeholder="Enter Twilio Account SID"
                        variant="outlined"
                        error={!!errors.account_sid}
                        helperText={errors.account_sid?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="auth_token"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Auth Token"
                        type={showAuthToken ? "text" : "password"}
                        placeholder="Enter Twilio Auth Token"
                        variant="outlined"
                        error={!!errors.auth_token}
                        helperText={errors.auth_token?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowAuthToken(!showAuthToken)}
                                edge="end"
                              >
                                {showAuthToken ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="sync_interval_hours"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Sync Interval (hours)"
                        type="number"
                        inputProps={{ step: 0.01, min: 0.01 }}
                        variant="outlined"
                        error={!!errors.sync_interval_hours}
                        helperText="Enter decimal hours (e.g., 0.01 for 36 seconds, 0.5 for 30 minutes, 2.5 for 2.5 hours)"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="page_size"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Page Size"
                        type="number"
                        variant="outlined"
                        error={!!errors.page_size}
                        helperText={errors.page_size?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Configuration Active"
                      />
                    )}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                    Enable this configuration for email reports and other operations. 
                    Must be active to send email reports.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="auto_sync_enabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAutoSyncToggle(e.target.checked);
                            }}
                          />
                        }
                        label="Auto Sync Enabled"
                      />
                    )}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                    Automatically sync Twilio data at the specified interval. 
                    Requires configuration to be active.
                  </Typography>
                </Grid>


              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Email Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="email_to"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={emailAddresses}
                        getOptionLabel={(option) => option.label ? `${option.label} <${option.email}>` : option.email}
                        value={field.value ? 
                          emailAddresses.filter(addr => 
                            field.value!.split(',').map(e => e.trim()).includes(addr.email)
                          ) : []
                        }
                        onChange={(_, newValue) => {
                          console.log('Email selection changed:', { newValue, fieldName: field.name });
                          const emails = newValue ? newValue.map(addr => addr.email).join(', ') : '';
                          console.log('Updated emails string:', emails);
                          field.onChange(emails);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="To Addresses"
                            placeholder="Select email addresses"
                            variant="outlined"
                            helperText="Select from your email address book"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.label ? `${option.label} <${option.email}>` : option.email}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="email_cc"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={emailAddresses}
                        getOptionLabel={(option) => option.label ? `${option.label} <${option.email}>` : option.email}
                        value={field.value ? 
                          emailAddresses.filter(addr => 
                            field.value!.split(',').map(e => e.trim()).includes(addr.email)
                          ) : []
                        }
                        onChange={(_, newValue) => {
                          console.log('Email selection changed:', { newValue, fieldName: field.name });
                          const emails = newValue ? newValue.map(addr => addr.email).join(', ') : '';
                          console.log('Updated emails string:', emails);
                          field.onChange(emails);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="CC Addresses"
                            placeholder="Select email addresses"
                            variant="outlined"
                            helperText="Select from your email address book"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.label ? `${option.label} <${option.email}>` : option.email}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="email_bcc"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={emailAddresses}
                        getOptionLabel={(option) => option.label ? `${option.label} <${option.email}>` : option.email}
                        value={field.value ? 
                          emailAddresses.filter(addr => 
                            field.value!.split(',').map(e => e.trim()).includes(addr.email)
                          ) : []
                        }
                        onChange={(_, newValue) => {
                          console.log('Email selection changed:', { newValue, fieldName: field.name });
                          const emails = newValue ? newValue.map(addr => addr.email).join(', ') : '';
                          console.log('Updated emails string:', emails);
                          field.onChange(emails);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="BCC Addresses"
                            placeholder="Select email addresses"
                            variant="outlined"
                            helperText="Select from your email address book"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.label ? `${option.label} <${option.email}>` : option.email}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Panel */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Control Panel
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={updateLoading || createLoading ? <CircularProgress size={20} /> : <Save />}
                  disabled={updateLoading || createLoading || !isDirty}
                >
                  {updateLoading || createLoading ? 'Saving...' : 'Save Configuration'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={syncLoading ? <CircularProgress size={20} /> : <Refresh />}
                  onClick={handleSync}
                  disabled={syncLoading || !selectedConfigId}
                >
                  {syncLoading ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={exportLoading ? <CircularProgress size={20} /> : <GetApp />}
                  onClick={handleExport}
                  disabled={exportLoading || !selectedConfigId}
                >
                  {exportLoading ? 'Exporting...' : 'Export Data'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={isEmailSending ? <CircularProgress size={20} /> : <Email />}
                  onClick={handleEmailReport}
                  disabled={isEmailSending || emailLoading || !selectedConfigId || !configurations?.find(config => config.id === selectedConfigId)?.is_active}
                  title={!selectedConfigId ? 'Select a configuration first' : 
                         !configurations?.find(config => config.id === selectedConfigId)?.is_active ? 
                         'Activate the selected configuration first' : 
                         'Send email report'}
                >
                  {isEmailSending ? 'Sending Email...' : 
                   !selectedConfigId ? 'Select Configuration First' :
                   !configurations?.find(config => config.id === selectedConfigId)?.is_active ? 
                   'Activate Configuration First' : 
                   'Send Email Report'}
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {statistics?.total_messages || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total SMS
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {statistics?.total_calls || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Calls
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {formatEasternTime(statistics?.last_sync_messages || autoSyncStatus?.last_auto_sync)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Sync (EST)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Chip
                      label={statistics?.is_active ? 'Active' : 'Inactive'}
                      color={statistics?.is_active ? 'success' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Status
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Runs Status */}
        {selectedConfigId && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Job Runs
                </Typography>
                {jobRunsLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : jobRuns && jobRuns.job_runs.length > 0 ? (
                  <Box>
                    <Grid container spacing={2}>
                      {jobRuns.job_runs.slice(0, 3).map((jobRun) => (
                        <Grid item xs={12} sm={6} md={4} key={jobRun.id}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2">
                                Job #{jobRun.id}
                              </Typography>
                              <Chip
                                label={jobRun.job_type}
                                color={jobRun.job_type === 'manual' ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Chip
                                label={jobRun.status}
                                color={
                                  jobRun.status === 'completed' ? 'success' :
                                  jobRun.status === 'failed' ? 'error' :
                                  jobRun.status === 'in_progress' ? 'info' : 'default'
                                }
                                size="small"
                              />
                              {jobRun.status === 'in_progress' && <CircularProgress size={16} />}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Messages: {jobRun.messages_synced} / {jobRun.total_messages}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Calls: {jobRun.calls_synced} / {jobRun.total_calls}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Started: {jobRun.started_at ? formatDateTime(jobRun.started_at, userTimezone) : 'N/A'}
                            </Typography>
                            {jobRun.error_message && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                {jobRun.error_message}
                              </Alert>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    {jobRunStats && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Statistics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Total Runs: {jobRunStats.total_runs}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Successful: {jobRunStats.successful_runs}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Failed: {jobRunStats.failed_runs}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              In Progress: {jobRunStats.in_progress_runs}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No job runs yet. Enable auto sync or run manual sync to see job runs.
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Data Tables */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent SMS Messages
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No SMS messages to display. Run sync to fetch data.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Calls
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No calls to display. Run sync to fetch data.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      </Box>

      {/* Success Notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Operation completed successfully!
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Email Confirmation Dialog */}
      <Snackbar
        open={showEmailConfirmation}
        autoHideDuration={null}
        onClose={() => setShowEmailConfirmation(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="info" 
          sx={{ 
            minWidth: '400px',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Email Report
            </Typography>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to send an email report for the selected configuration?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will send a report with CSV attachments to the configured email recipients.
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmEmailReport}
                disabled={isEmailSending}
              >
                Yes, Send Report
              </Button>
              <Button
                variant="outlined"
                onClick={cancelEmailReport}
                disabled={isEmailSending}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
};
